import type { Color } from "@mui/material";
import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { Provider, useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle.ts";
import { ApArAgingSummaryReportType } from "../../../../../api/types/svTypes.ts";
import DrillDownEntry from "../../sv/components/builder/common/drillDown/DrillDownEntry.tsx";
import { DeveloperPanelWrapper } from "../../sv/components/builder/dev/DeveloperPanelWrapper.tsx";
import { BiCommonDataContextProvider } from "../../sv/contexts/BiCommonDataContextProvider.tsx";
import { selectRefreshToken } from "../../sv/store/selectors/currentReportSelectors.ts";
import {
  selectCurrentDataSetId,
  selectDimensions,
  selectDimensionsStructure,
  selectMeasures,
} from "../../sv/store/selectors/metaDataSelectors.ts";
import { store } from "../../sv/store/store.ts";
import { changeDataSet } from "../../sv/store/thunks/metaDataThunks.ts";
import { DataSetIds } from "../../sv/utilities/DimensionNames.ts";
import { standardReportsByKey } from "../standardReportsDefinitions.tsx";
import AgingSummaryView from "./AgingSummaryView.tsx";
import { AgingSummaryContext } from "./contexts/AgingSummaryContext.tsx";
import useAgingSummaryState from "./hooks/useAgingSummaryState.ts";

const REPORT_ID = "ap-ar-aging-summary";

interface AgingSummaryReportConfig {
  title: string;
  loadErrorTitle: string;
  icon: React.ReactNode;
  iconColor: Color;
  reportType: ApArAgingSummaryReportType;
}

const createAgingSummaryReport = (config: AgingSummaryReportConfig) => {
  const AgingSummaryReportPage = () => {
    usePageTitle(config.title);

    const state = useAgingSummaryState({ reportType: config.reportType });
    const dimensions = useSelector(selectDimensions);
    const dimensionsStructure = useSelector(selectDimensionsStructure);
    const measures = useSelector(selectMeasures);
    const dataSetId = useSelector(selectCurrentDataSetId) ?? DataSetIds.GeneralAndMemo;
    const refreshToken = useSelector(selectRefreshToken);

    if (state.isLoading) {
      return <InlineLoader />;
    }

    if (state.isLoadingFailed) {
      return <DataLoadingFailed title={config.loadErrorTitle} />;
    }

    return (
      <AgingSummaryContext value={state}>
        <BiCommonDataContextProvider
          books={[]}
          isMultiBook={false}
          dataSetId={dataSetId}
          dimensionsStructure={dimensionsStructure}
          dimensions={dimensions}
          editMode={false}
          conditions={[]}
          reportId={REPORT_ID}
          refreshToken={refreshToken}
          dimensionNames={dimensions.map((d) => d.name)}
          measureNames={measures.map((m) => m.name)}
          onConditionUpdate={() => {}}
          onRefreshGrid={state.refresh}
        >
          <AgingSummaryView
            reportTitle={config.title}
            reportId={REPORT_ID}
            reportIcon={config.icon}
            reportIconColor={config.iconColor}
          />
          <DrillDownEntry reportId={REPORT_ID} />
        </BiCommonDataContextProvider>
      </AgingSummaryContext>
    );
  };

  const AgingSummaryReportPageWithStore = () => {
    const [isStoreInitialized, setIsStoreInitialized] = useState(false);

    useEffect(() => {
      let isActive = true;

      const initializeStore = async () => {
        await store.dispatch(changeDataSet(DataSetIds.GeneralAndMemo));
        if (isActive) {
          setIsStoreInitialized(true);
        }
      };

      void initializeStore();

      return () => {
        isActive = false;
      };
    }, []);

    if (!isStoreInitialized) {
      return <InlineLoader />;
    }

    return (
      <Provider store={store}>
        <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
          <AgingSummaryReportPage />
          <DeveloperPanelWrapper />
        </Stack>
      </Provider>
    );
  };

  return AgingSummaryReportPageWithStore;
};

export const ApAgingSummaryReportPage = createAgingSummaryReport({
  title: "A/P Aging Summary Report",
  loadErrorTitle: "Failed to load A/P aging summary report",
  icon: <standardReportsByKey.apAgingSummary.Icon />,
  iconColor: standardReportsByKey.apAgingSummary.iconColor,
  reportType: ApArAgingSummaryReportType.AccountsPayable,
});

export const ArAgingSummaryReportPage = createAgingSummaryReport({
  title: "A/R Aging Summary Report",
  loadErrorTitle: "Failed to load A/R aging summary report",
  icon: <standardReportsByKey.arAgingSummary.Icon />,
  iconColor: standardReportsByKey.arAgingSummary.iconColor,
  reportType: ApArAgingSummaryReportType.AccountsReceivable,
});
